import React, { useState, useEffect } from 'react';
import { GaugeCircle, Loader2, Brain, Shield, ShieldUser, PersonStanding, Smile, Landmark } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const DIALS = [
	{ key: 'Security', label: 'Security', color: 'from-emerald-500 to-emerald-300', icon: Shield, iconColor:'text-emerald-500' },
	{ key: 'Privacy', label: 'Privacy', color: 'from-cyan-400 to-blue-200', icon: ShieldUser, iconColor:'text-cyan-400' },
	{ key: 'Accessibility', label: 'Accessibility', color: 'from-amber-500 to-amber-300', icon: PersonStanding, iconColor:'text-amber-500' },
	{ key: 'Customer Experience', label: 'Customer Experience', color: 'from-emerald-500 to-emerald-300', icon: Smile, iconColor:'text-emerald-500' },
	{ key: 'Governance', label: 'Governance', color: 'from-red-500 to-red-300', icon: Landmark, iconColor:'text-red-500' },
];

function clamp(val, min, max) {
	return Math.max(min, Math.min(max, val));
}

const RiskDial = ({ label, value, color, icon, iconColor }) => {
	// value: 0-100
	const radius = 72;
	const stroke = 10;
	const normalizedRadius = radius - stroke / 2;
	const circumference = 2 * Math.PI * normalizedRadius;
	const percent = clamp(value, 0, 100);
	const offset = circumference - (percent / 100) * circumference;
    const Icon = icon;
	return (
		<div className="dial-container flex flex-col items-center">
			<div className="risk-dial-wrapper relative w-40 h-40 flex items-center justify-center mb-2">
				<svg width={radius * 2} height={radius * 2} className={`risk-dial ${color}`}>
					<circle
						className="dial-ring"
						stroke="#e5e7eb"
						fill="none"
						strokeWidth={stroke}
						cx={radius}
						cy={radius}
						r={normalizedRadius}
					/>
					<circle
						className="dial-ring"
						strokeDasharray={circumference}
						strokeDashoffset={offset}
						strokeLinecap="round"
						stroke={`url(#${label.replace(/\s+/g, '-')}-gradient)`}
						fill="none"
						strokeWidth={stroke}
						cx={radius}
						cy={radius}
						r={normalizedRadius}
						style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.4,2,.6,1)' }}
					/>
					<defs>
						<linearGradient id={`${label.replace(/\s+/g, '-')}-gradient`} x1="0" y1="0" x2="1" y2="1">
							{/* <stop offset="0%" stopColor={color.split(' ')[0].replace('from-', '')} />
							<stop offset="100%" stopColor={color.split(' ')[1].replace('to-', '')} /> */}
                            {/* Tailwind v3 uses these specific variable names internally */}
                            <stop offset="0%" stopColor="var(--tw-gradient-from)" />
                            <stop offset="100%" stopColor="var(--tw-gradient-to)" />
						</linearGradient>
					</defs>
				</svg>
				<span className="dial-value absolute inset-0 flex items-center justify-center text-2xl font-bold text-gray-800 dark:text-white">
					{percent}%
				</span>
			</div>
			{/* <span className="dial-label text-xs text-black-400 font-bold">{label}</span> */}
            <span className="dial-label flex items-center text-xs text-black-400 font-bold">
                <Icon className={`w-4 h-4 mr-1 ${iconColor}`} />{label}
            </span>
        </div>
	);
};

const RiskDialTab = ({ scanId }) => {
	const [riskDialData, setRiskDialData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [cached, setCached] = useState(false);
	const [tokensUsed, setTokensUsed] = useState(null);
	const [costUsd, setCostUsd] = useState(null);
	const [lastUpdated, setLastUpdated] = useState(null);

	useEffect(() => {
		const fetchCachedRiskDial = async () => {
			setLoading(true);
			setError(null);
			try {
				const cachedResponse = await fetch(ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Risk Dial"), { method: 'POST', credentials: 'include' });
				if (cachedResponse.status == 200) {
					const json = await cachedResponse.json();
					const data = JSON.parse(json.response);
					setRiskDialData(data);
					setTokensUsed(json.tokens_used);
					setCostUsd(json.cost_usd);
					setLastUpdated(json.lastUpdated);
					setCached(true);
				} else {
					setCached(false);
				}
			} catch (err) {
				setError('Failed to load Risk Dial data.');
			} finally {
				setLoading(false);
			}
		};
		fetchCachedRiskDial();
	}, [scanId]);

	const handleGenerate = async () => {
		setLoading(true);
		setError(null);
		setCached(false);
		try {
			const response = await fetch(ApiNames.AI_HUB_GENERATE_RISK_DIAL(scanId), { method: 'POST', credentials: 'include' });
			if (!response.ok) throw new Error('Request failed');
			const json = await response.json();
			const data = JSON.parse(json.response);
			setRiskDialData(data);
			setTokensUsed(json.tokens_used);
			setCostUsd(json.cost_usd);
			setLastUpdated(json.lastUpdated);
		} catch (err) {
			setError('Unable to load Risk Dial data. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex flex-col items-center gap-8">
			<div className="w-full max-w-6xl">
				<div className="card-header flex items-center justify-between mb-2">
					<div className="card-title flex items-center gap-2">
						<GaugeCircle className="w-6 h-6 text-blue-400" />
						<h3 className="font-bold text-lg">AI Executive Risk Dial - 222 Controls → 5 Strategic Areas</h3>
					</div>
					<span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold">
						<Brain className="w-4 h-4 mr-1" />AI-Weighted
					</span>
				</div>
				<p className="text-gray-400 mb-6 text-sm mt-2">
					{/* Visualize risk levels across key compliance domains. The Risk Dial provides a quick snapshot of your organization's risk posture and rationale for each area. */}AI compresses 222 individual controls into 5 strategic risk areas using weighted scoring based on threat landscape, business impact, and regulatory priority—not naive averages.
				</p>
				<div className="w-full flex flex-col items-center">
					{!cached && !riskDialData && !loading && !error && (
						<div className="flex flex-col items-center py-12">
							<button
								className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
								onClick={handleGenerate}
								disabled={loading || !scanId}
							>
								Generate Risk Dial
							</button>
						</div>
					)}
					{loading && (
						<div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
							<Loader2 className="animate-spin text-yellow-400 w-10 h-10 mb-4" />
							<span className="text-gray-400">Generating Risk Dial...</span>
						</div>
					)}
					{error && !loading && (
						<div className="ai-response text-center text-red-400 py-12">{error}</div>
					)}
					{riskDialData && !loading && !error && (
						<>
							<div className="flex flex-wrap justify-center gap-8 mb-6">
								{DIALS.map(dial => (
									<RiskDial
										key={dial.key}
										label={dial.label}
										value={riskDialData[dial.key] ?? 0}
										color={dial.color}
                                        icon={dial.icon}
                                        iconColor={dial.iconColor}
									/>
								))}
							</div>
							<div className="ai-response bg-base-100 rounded-xl shadow-xl p-6 animate-fadeIn mb-4 w-full">
								<h4 className="font-bold text-lg mb-2">AI Weighting Rationale</h4>
								<p className="mb-4 text-black-400" style={{whiteSpace: 'pre-line'}}>{riskDialData.rationale}</p>
								<p className="mb-2 text-gray-400">Tokens used: {tokensUsed}. Cost: ${costUsd}.</p>
								{cached && (
									<p className="mb-2 text-gray-400">Viewing cached Risk Dial. Last updated: {lastUpdated}.</p>
								)}
								<button
									className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
									onClick={handleGenerate}
									disabled={loading || !scanId}
								>
									Regenerate Risk Dial
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default RiskDialTab;
